import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  postMethod,
  getMethod,
  patchMethod,
  postMethod2,
  patchMethod2,
} from "../../services/ApiCallMethods";

// POST: Add a new client
export const addClientManagement = createAsyncThunk(
  "clientManagement/addClient",
  async (clientPayload, { rejectWithValue }) => {
    try {
      const response = await postMethod(`/api/addUser`, clientPayload);
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || "Something went wrong");
    }
  }
);

// GET: Fetch client list
export const getClientList = createAsyncThunk(
  "clientManagement/getClients",
  async (
    {
      currentPage,
      itemsPerPage,
      isActive,
      fromDate,
      toDate,
      securityId,
      accountId,
      investmentType,
      investmentAmount,
      search,
      NoPagination
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();

      if (isActive) params.append("isActive", isActive);

      if (fromDate) params.append("fromDate", fromDate);
      if (toDate) params.append("toDate", toDate);
      if (investmentType) params.append("investments", investmentType); // API expects `investments`
      if (investmentAmount)
        params.append("investment_amount", investmentAmount);
      if (search) params.append("search", search);
       if(accountId) params.append("accountId",accountId)
        if(securityId) params.append("productId",securityId)
      if (NoPagination===false) params.append("NoPagination",false)
      params.append("page", currentPage || 1);
      params.append("limit", itemsPerPage || 1000);


      const url = `/api/clients?${params.toString()}`;

      const response = await getMethod(url);
      const json = await response.json();

      return json;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch clients");
    }
  }
);

//PATCH: Update a client
export const updateClientManagement = createAsyncThunk(
  "clientManagement/updateClient",
  async (clientPayload, { rejectWithValue }) => {
    try {
      const response = await patchMethod(
        `/api/clients/${clientPayload.id}`,
        clientPayload
      );
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data?.message || "Something went wrong");
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const updateClientManagementProfile = createAsyncThunk(
  "clientManagement/updateClient",
  async ({ clientId, profileData }, { rejectWithValue }) => {
    try {
      const response = await patchMethod2(
        `/api/clients/${clientId}`,
        profileData
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to update client");
      }

      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to update client");
    }
  }
);

// PATCH: Update client active status
export const updateClientStatus = createAsyncThunk(
  "clientManagement/updateClientStatus",
  async ({ id, is_active }, { rejectWithValue }) => {
    try {
      const response = await patchMethod(`/api/users/${id}/status`, {
        is_active,
      });
      const data = await response.json();
      return data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update status");
    }
  }
);

// GET: Fetch financial list
export const getFinancialAdvisor = createAsyncThunk(
  "clientManagement/getfinancialadvisor",
  async (
    { id, search, page = 1, limit = 10, investment, investment_amount },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams({
        search: search || "",
        page: page.toString(),
        limit: limit.toString(),
      });

      if (investment) {
        queryParams.append("investmentType", investment);
      }
      if (investment_amount) {
        queryParams.append("investmentAmount", investment_amount.toString());
      }

      const response = await getMethod(
        `/api/clients/${id}/advisors?${queryParams.toString()}`
      );
      const json = await response.json();

      return {
        data: json.advisors || [],
        pagination: json.pagination || {},
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch financial advisors"
      );
    }
  }
);


export const bulkImport = createAsyncThunk(
  "clientManagement/bulkinvite",
  async (filePayload, { rejectWithValue }) => {
    try {
      const response = await postMethod2("/api/users/bulk-import", filePayload);
      const json = await response.json();
      return json;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch clients");
    }
  }
);

export const searchClients = createAsyncThunk(
  "clientManagement/search",
  async (
    { currentPage, itemsPerPage, isActive, fromDate, toDate, value },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      if (isActive !== null) params.append("isActive", isActive);
      params.append("fromDate", fromDate);
      params.append("toDate", toDate);
      params.append("page", currentPage);
      params.append("limit", itemsPerPage);
      params.append("search", value);
      const url = `/api/clients?${params.toString()}`;
      const response = await getMethod(url);
      const json = await response.json();
      return json.data || json;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Advisor Not Found");
    }
  }
);

export const getClientById = createAsyncThunk(
  "clientManagement/getClientById",
  async (clientId, { rejectWithValue }) => {
    try {
      const response = await getMethod(`/api/users/${clientId}`);
      const json = await response.json();
      return json.data || json;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Advisor Not Found");
    }
  }
);

export const changePasswordRequest = createAsyncThunk(
  "clientManagement/changePasswordRequest",
  async ({ email, userId }, { rejectWithValue }) => {
    try {
      const response = await postMethod("/api/changePasswordrequest", { email, userId });
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data);
      }
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || "Something went wrong");
    }
  }
);

// Initial state
const initialState = {
  loading: false,
  error: null,
  success: false,
  data: null,
  clients: [],
  advisors: [],
  clientsLoading: false,
  advisorLoading: false,
  advisorError: null,
  clientsError: null,
  bulkinvite: null,
  pagination: {},
  searchResult: [],
  clientById: {},
  advisorsPagination: {},
  clientLoading: false,
  view: "Financial Advisors",
};

// Slice
const clientManagementSlice = createSlice({
  name: "clientManagement",
  initialState,
  reducers: {
    resetClientManagement: (state) => {
      state.loading = false;
      state.success = false;
      state.error = null;
      state.data = null;
    },
    setView: (state, action) => {
      state.view = action.payload;
    },
  },
  extraReducers: (builder) => {
    // POST: Add client
    builder
      .addCase(addClientManagement.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(addClientManagement.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.data = action.payload;
      })
      .addCase(addClientManagement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
        state.success = false;
      });

    // GET: Client list
    builder
      .addCase(getClientList.pending, (state) => {
        state.clientsLoading = true;
        state.clientsError = null;
        state.clients = []; // Clear old data
        state.pagination = {}; // Clear old pagination
      })
      .addCase(getClientList.fulfilled, (state, action) => {
        state.clientsLoading = false;
        state.clients = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(getClientList.rejected, (state, action) => {
        state.clientsLoading = false;
        state.clientsError = action.payload;
      });

    // PATCH: update client
    builder
      .addCase(updateClientManagement.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateClientManagement.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.data = action.payload;
      })
      .addCase(updateClientManagement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });

    // PATCH: Update client status (active/inactive)
    builder
      .addCase(updateClientStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateClientStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.data = action.payload;
      })
      .addCase(updateClientStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });

    // GET: financial advisor list
    builder
      .addCase(getFinancialAdvisor.pending, (state) => {
        state.advisorLoading = true;
        state.advisorError = null;
        state.advisors = []; // Clear old data
        state.advisorsPagination = {}; // Clear old pagination
      })
      .addCase(getFinancialAdvisor.fulfilled, (state, action) => {
        state.advisorLoading = false;
        // Ensure we always set the exact data from the API response
        state.advisors = action.payload.data || [];
        state.advisorsPagination = action.payload.pagination || {};
      })

      .addCase(getFinancialAdvisor.rejected, (state, action) => {
        state.advisorLoading = false;
        state.advisorError = action.payload;
      });

    // BULK IMPORT Functionality
    builder
      .addCase(bulkImport.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkImport.fulfilled, (state, action) => {
        state.loading = false;
        state.bulkinvite = action.payload;
      })
      .addCase(bulkImport.rejected, (state, action) => {
        state.loading = false;
      });

    builder
      .addCase(searchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload;
        state.error = null;
      })
      .addCase(searchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(getClientById.pending, (state) => {
        state.clientLoading = true;
        state.error = null;
      })
      .addCase(getClientById.fulfilled, (state, action) => {
        state.clientLoading = false;
        state.clientById = action.payload;
        state.error = null;
      })
      .addCase(getClientById.rejected, (state, action) => {
        state.clientLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(changePasswordRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePasswordRequest.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(changePasswordRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetClientManagement, setView } = clientManagementSlice.actions;
export default clientManagementSlice.reducer;
